import { useState, useEffect, useCallback } from 'react'
import { MarketTabType } from '../components/market/MarketTabs'
import { useMarketState, defaultState } from '../contexts/MarketStateContext'

interface UseMarketTabsParams {
  modelId: string | undefined
  loading: boolean
  transactionsLoading: boolean
  eventsLoading: boolean
}

export function useMarketTabs({ modelId, loading, transactionsLoading, eventsLoading }: UseMarketTabsParams) {
  const { getMarketState, setMarketState } = useMarketState()

  const [expandedTables, setExpandedTables] = useState(defaultState.expandedTables)
  const [hasRestoredState, setHasRestoredState] = useState(false)
  // Active market tab state - controls which table is visible
  const [activeTab, setActiveTab] = useState<MarketTabType>('for-sale')
  // Track window scroll positions for each table tab
  const [tableScrollPositions, setTableScrollPositions] = useState<{ forSale: number; sold: number; removed: number }>(
    defaultState.tableScrollPositions
  )
  // Aircraft ID to scroll to when returning from detail page
  const [pendingScrollToAircraftId, setPendingScrollToAircraftId] = useState<number | null>(null)
  // Unique row ID to scroll to (for sold/removed tables with transaction/event IDs)
  const [pendingScrollToRowId, setPendingScrollToRowId] = useState<string | null>(null)

  // Restore saved state on mount
  const restoreState = useCallback(() => {
    if (!modelId || hasRestoredState) return
    const savedState = getMarketState(modelId)
    if (savedState) {
      setExpandedTables(savedState.expandedTables)
      if (savedState.activeTab) {
        setActiveTab(savedState.activeTab)
      }
      if (savedState.tableScrollPositions) {
        setTableScrollPositions(savedState.tableScrollPositions)
      }
      if (savedState.lastViewedAircraftId || savedState.lastViewedRowId) {
        setPendingScrollToAircraftId(savedState.lastViewedAircraftId ?? null)
        setPendingScrollToRowId(savedState.lastViewedRowId ?? null)
      } else {
        window.scrollTo(0, 0)
      }
    }
    setHasRestoredState(true)
  }, [modelId, hasRestoredState, getMarketState])

  // Scroll to aircraft row when data is loaded and we have a pending scroll target
  useEffect(() => {
    if (!pendingScrollToAircraftId && !pendingScrollToRowId) return

    let isDataLoaded = false
    switch (activeTab) {
      case 'for-sale': isDataLoaded = !loading; break
      case 'sold': isDataLoaded = !transactionsLoading; break
      case 'removed': isDataLoaded = !eventsLoading; break
    }

    if (isDataLoaded) {
      const attemptScroll = (attempt: number) => {
        let aircraftRow: Element | null = null

        if (pendingScrollToRowId) {
          const rowSelector = `tr[data-row-id="${pendingScrollToRowId}"]`
          aircraftRow = document.querySelector(rowSelector)
        }

        if (!aircraftRow && pendingScrollToAircraftId) {
          const aircraftSelector = `tr[data-aircraft-id="${pendingScrollToAircraftId}"]`
          aircraftRow = document.querySelector(aircraftSelector)
        }

        if (aircraftRow) {
          const headerOffset = 340
          const elementPosition = aircraftRow.getBoundingClientRect().top + window.scrollY
          window.scrollTo({ top: elementPosition - headerOffset, behavior: 'auto' })
          setPendingScrollToAircraftId(null)
          setPendingScrollToRowId(null)
        } else if (attempt < 5) {
          setTimeout(() => attemptScroll(attempt + 1), (attempt + 1) * 100)
        } else {
          setPendingScrollToAircraftId(null)
          setPendingScrollToRowId(null)
        }
      }

      const scrollTimeout = setTimeout(() => attemptScroll(1), 150)
      return () => clearTimeout(scrollTimeout)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingScrollToAircraftId, pendingScrollToRowId, activeTab, loading, transactionsLoading, eventsLoading])

  // Helper to read lastViewedAircraftId/lastViewedRowId from sessionStorage
  const readLastViewedFromStorage = useCallback((mid: string) => {
    let lastViewedAircraftId: number | undefined
    let lastViewedRowId: string | undefined
    try {
      const rawStates = sessionStorage.getItem('marketStates')
      if (rawStates) {
        const states = JSON.parse(rawStates)
        lastViewedAircraftId = states[mid]?.lastViewedAircraftId
        lastViewedRowId = states[mid]?.lastViewedRowId
      }
    } catch (e) {
      // Failed to read from sessionStorage
    }
    return { lastViewedAircraftId, lastViewedRowId }
  }, [])

  // Handle tab changes - save current tab's window scroll, switch tab, restore new tab's scroll
  const handleTabChange = useCallback((newTab: MarketTabType) => {
    const currentWindowScroll = window.scrollY
    const newScrollPositions = { ...tableScrollPositions }
    switch (activeTab) {
      case 'for-sale': newScrollPositions.forSale = currentWindowScroll; break
      case 'sold': newScrollPositions.sold = currentWindowScroll; break
      case 'removed': newScrollPositions.removed = currentWindowScroll; break
    }
    setTableScrollPositions(newScrollPositions)

    let newTabScrollPosition = 0
    switch (newTab) {
      case 'for-sale': newTabScrollPosition = newScrollPositions.forSale; break
      case 'sold': newTabScrollPosition = newScrollPositions.sold; break
      case 'removed': newTabScrollPosition = newScrollPositions.removed; break
    }

    if (modelId) {
      const { lastViewedAircraftId, lastViewedRowId } = readLastViewedFromStorage(modelId)
      setMarketState(modelId, {
        scrollPosition: currentWindowScroll,
        activeTab: newTab,
        expandedTables,
        tableScrollPositions: newScrollPositions,
        lastViewedAircraftId,
        lastViewedRowId
      })
    }

    setActiveTab(newTab)
    setTimeout(() => { window.scrollTo(0, newTabScrollPosition) }, 0)
  }, [modelId, expandedTables, setMarketState, activeTab, tableScrollPositions, readLastViewedFromStorage])


  // Function to save state before navigating to detail pages
  const handleNavigateToDetail = useCallback((aircraftId: number, rowId: string) => {
    if (modelId) {
      const currentWindowScroll = window.scrollY
      const newScrollPositions = { ...tableScrollPositions }
      switch (activeTab) {
        case 'for-sale': newScrollPositions.forSale = currentWindowScroll; break
        case 'sold': newScrollPositions.sold = currentWindowScroll; break
        case 'removed': newScrollPositions.removed = currentWindowScroll; break
      }

      setMarketState(modelId, {
        scrollPosition: currentWindowScroll,
        activeTab,
        expandedTables,
        tableScrollPositions: newScrollPositions,
        lastViewedAircraftId: aircraftId,
        lastViewedRowId: rowId
      })
    }
  }, [modelId, activeTab, expandedTables, tableScrollPositions, setMarketState])

  // Save state when expanded tables change (after initial restore)
  useEffect(() => {
    if (modelId && hasRestoredState) {
      const { lastViewedAircraftId, lastViewedRowId } = readLastViewedFromStorage(modelId)
      const currentWindowScroll = window.scrollY
      const updatedScrollPositions = { ...tableScrollPositions }
      switch (activeTab) {
        case 'for-sale': updatedScrollPositions.forSale = currentWindowScroll; break
        case 'sold': updatedScrollPositions.sold = currentWindowScroll; break
        case 'removed': updatedScrollPositions.removed = currentWindowScroll; break
      }
      setMarketState(modelId, {
        scrollPosition: currentWindowScroll,
        activeTab,
        expandedTables,
        tableScrollPositions: updatedScrollPositions,
        lastViewedAircraftId,
        lastViewedRowId
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, hasRestoredState, expandedTables, setMarketState])

  // Save state before unmounting or when modelId changes
  useEffect(() => {
    return () => {
      if (modelId) {
        const { lastViewedAircraftId, lastViewedRowId } = readLastViewedFromStorage(modelId)
        const currentWindowScroll = window.scrollY
        const updatedScrollPositions = { ...tableScrollPositions }
        switch (activeTab) {
          case 'for-sale': updatedScrollPositions.forSale = currentWindowScroll; break
          case 'sold': updatedScrollPositions.sold = currentWindowScroll; break
          case 'removed': updatedScrollPositions.removed = currentWindowScroll; break
        }
        setMarketState(modelId, {
          scrollPosition: currentWindowScroll,
          activeTab,
          expandedTables,
          tableScrollPositions: updatedScrollPositions,
          lastViewedAircraftId,
          lastViewedRowId
        })
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, expandedTables, activeTab, tableScrollPositions])

  return {
    activeTab,
    expandedTables,
    setExpandedTables,
    hasRestoredState,
    restoreState,
    handleTabChange,
    handleNavigateToDetail,
  }
}